import { Notification } from "@/utils/notificationUtils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { calcBlackListApi } from "../../../modules/calculator/blacklist/api";
import { CalcBlacklistFilterPageRequest, CreateUpdateCalcBlacklist } from "../../../modules/calculator/blacklist/types";
import { UUID } from "../../../typings/global";
import { EntityObject } from "../../types";

export const CALC_BLACK_LIST_QUERY_KEYS = {
  filterCalcBlacklists: (payload?: CalcBlacklistFilterPageRequest) =>
    payload ? ["filterCalcBlacklists", payload] : ["filterCalcBlacklists"],
  createCalcBlacklist: () => ["createCalcBlacklist"],
  updateCalcBlacklist: () => ["updateCalcBlacklist"],
  deleteCalcBlacklist: () => ["deleteCalcBlacklist"],
};

export const useFilterCalcBlackLists = (payload: CalcBlacklistFilterPageRequest) => {
  return useQuery({
    queryKey: CALC_BLACK_LIST_QUERY_KEYS.filterCalcBlacklists(payload),
    queryFn: async () => {
      const { data } = await calcBlackListApi.filterCalcBlacklists(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useCreateCalcBlackList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CALC_BLACK_LIST_QUERY_KEYS.createCalcBlacklist(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateUpdateCalcBlacklist) => {
      const { data } = await calcBlackListApi.createCalcBlacklist(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CALC_BLACK_LIST_QUERY_KEYS.filterCalcBlacklists() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateCalcBlackList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CALC_BLACK_LIST_QUERY_KEYS.updateCalcBlacklist(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateCalcBlacklist>) => {
      const { data } = await calcBlackListApi.updateCalcBlacklist(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CALC_BLACK_LIST_QUERY_KEYS.filterCalcBlacklists() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteCalcBlackList = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: CALC_BLACK_LIST_QUERY_KEYS.deleteCalcBlacklist(),
    mutationFn: (id: UUID) => {
      return calcBlackListApi.deleteCalcBlacklist({ id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CALC_BLACK_LIST_QUERY_KEYS.filterCalcBlacklists() });
      Notification.itemDeletedNotification();
    },
  });
};
